import { Box, Flex, Image, Skeleton, Text } from '@chakra-ui/react';
import type { CardProps } from '@ui/components/Card';
import { Card } from '@ui/components/Card';
import { InfoTooltip } from '@ui/components/InfoTooltip';
import { LPTokenIcon } from '@ui/components/LPTokenIcon';
import type { CoinMode } from '@ui/hooks/useCoinMode';
import { useHasNftHelperCollections } from '@ui/hooks/useCruisers';
import useFarmAprs from '@ui/hooks/useFarmAprs';
import useGardenApy from '@ui/hooks/useGardenApy';
import useLpAmountsStaked from '@ui/hooks/useLpAmountsStaked';
import { useTranslations } from '@ui/i18n';
import { useVenoSDK } from '@ui/providers/VenoSDKProvider';
import { formatTokenAmount } from '@ui/utils/format';
import type { GardenType } from '@ui/utils/garden';
import {
  formatGardenApy,
  getGardenAvatarUrl,
  getGardenColorThemeByCoinMode,
  getGardenName,
} from '@ui/utils/garden';
import React, { useMemo } from 'react';

import { GardenBooster } from './GardenBooster';
import GardenNftOverview from './GardenNftOverview';
import { useGardenModalStore } from './useGardenModalStore';

export type GardenCardProps = CardProps & {
  coinMode: CoinMode;
  gardenType: GardenType;
};

function GardenCard({ coinMode, gardenType, ...props }: GardenCardProps) {
  const t = useTranslations();
  const openModal = useGardenModalStore((s) => s.open);
  const { image, apy, locked, isBoosted } = useGardenCardData({
    gardenType,
  });

  const hasNftHelperCollections = useHasNftHelperCollections();

  return (
    <Card
      as={Flex}
      colorTheme={getGardenColorThemeByCoinMode(coinMode)}
      justifyContent="space-between"
      rounded="5px"
      gap={4}
      cursor="pointer"
      m="0"
      p="10px 10px 12px"
      onClick={() => {
        openModal({
          type: gardenType,
          activeView: 'PLANT',
          coinMode,
        });
      }}
      {...props}
    >
      <Flex flexDir="column" alignItems="center">
        <Flex
          w="60px"
          h="60px"
          justify="center"
          align="center"
          rounded="full"
          borderWidth="2px"
          bgColor="brand-white"
          borderColor="brand-background"
          _dark={{
            borderColor: 'brand-inactive',
          }}
        >
          <Image src={image} alt="Brand Image" maxW="100%" maxH="100%" />
        </Flex>
        {isBoosted && hasNftHelperCollections && <GardenBooster mt="10px" />}
      </Flex>
      <Flex flex={1} flexDir="row" justify="space-between">
        <Flex flexDir="column">
          <Text textStyle="h4Bold">
            {getGardenName(coinMode, gardenType, false)}
          </Text>
          {apy ? (
            <Flex align="center" mt={1} textStyle="body" color="text-light">
              APR:
              <Text
                fontWeight="bold"
                ml={1}
                color={isBoosted ? 'garden.highlight' : 'text.normal'}
              >
                {apy}
              </Text>{' '}
              {gardenType === 'VVS_TIA' && (
                <InfoTooltip
                  title={t('Liquidity Provider')}
                  label={
                    <Text
                      px={{ base: 0, desktop: 8 }}
                      py={{ base: 0, desktop: 4 }}
                    >
                      {t('liquidity-provider-explanation-{protocol}', {
                        protocol: 'VVS',
                      })}
                    </Text>
                  }
                  color="text-active"
                  tooltipProps={{
                    shadow: 'card.normal',
                  }}
                />
              )}
            </Flex>
          ) : (
            <Skeleton mt={1} h="24px" w="65px" />
          )}
          {locked ? (
            <LPTokenIcon
              coinMode={coinMode}
              gardenType={gardenType}
              mt={3}
              textStyle="bodyBold"
            >
              {locked}
            </LPTokenIcon>
          ) : (
            <Skeleton mt={1.5} h="24px" w="60px" />
          )}
        </Flex>
        <Flex justifySelf="flex-start">
          <Box>
            {isBoosted && !hasNftHelperCollections && <GardenBooster />}
          </Box>
          <Box>
            {hasNftHelperCollections && (
              <GardenNftOverview gardenType={gardenType} />
            )}
          </Box>
        </Flex>
      </Flex>
    </Card>
  );
}

export default GardenCard;

const useGardenCardData = ({ gardenType }: { gardenType: GardenType }) => {
  const sdk = useVenoSDK();
  const { isBoosted } = useFarmAprs();
  const { stakedFerro, stakedTectonic, stakedVvs, stakedVvsCro, stakedVvsTia } =
    useLpAmountsStaked();
  const {
    ferroGardenApy,
    tectonicGardenApy,
    vvsGardenApy,
    vvsCroGardenApy,
    vvsTiaGardenApy,
  } = useGardenApy();
  const gardenAvatarUrl = getGardenAvatarUrl(sdk.chainType, gardenType);

  return useMemo(() => {
    if (gardenType === 'FERRO') {
      return {
        image: gardenAvatarUrl,
        gardenType: 'FERRO',
        apy: formatGardenApy(ferroGardenApy),
        locked: stakedFerro
          ? (formatTokenAmount(stakedFerro) as string)
          : undefined,
        isBoosted,
      };
    }

    if (gardenType === 'VVS') {
      return {
        image: gardenAvatarUrl,
        gardenType: 'VVS',
        apy: formatGardenApy(vvsGardenApy),
        locked: stakedVvs
          ? (formatTokenAmount(stakedVvs) as string)
          : undefined,
        isBoosted,
      };
    }

    if (gardenType === 'VVS_CRO') {
      return {
        image: gardenAvatarUrl,
        gardenType: 'VVS_CRO',
        apy: formatGardenApy(vvsCroGardenApy),
        locked: stakedVvsCro
          ? (formatTokenAmount(stakedVvsCro) as string)
          : undefined,
        isBoosted,
      };
    }

    if (gardenType === 'VVS_TIA') {
      return {
        image: gardenAvatarUrl,
        gardenType: 'VVS_TIA',
        apy: formatGardenApy(vvsTiaGardenApy),
        locked: stakedVvsTia
          ? (formatTokenAmount(stakedVvsTia) as string)
          : undefined,
        isBoosted,
      };
    }

    return {
      image: gardenAvatarUrl,
      gardenType: 'TECTONIC',
      apy: formatGardenApy(tectonicGardenApy),
      isBoosted,
      locked: stakedTectonic
        ? (formatTokenAmount(stakedTectonic, 4, 8) as string)
        : undefined,
    };
  }, [
    ferroGardenApy,
    gardenAvatarUrl,
    gardenType,
    isBoosted,
    stakedFerro,
    stakedTectonic,
    stakedVvs,
    stakedVvsCro,
    stakedVvsTia,
    tectonicGardenApy,
    vvsCroGardenApy,
    vvsGardenApy,
    vvsTiaGardenApy,
  ]);
};
